import { useEffect, useMemo, useState } from "react";
import { getCategoryLabel, QUESTION_SUBTYPES } from "../../shared/questionTaxonomy";
import type { DifficultyLevel, QuestionItem, SanitizedQuestionItem, TestCategory } from "../../shared/types";
import { AnswerGrid } from "../components/test/AnswerGrid";
import { VisualQuestionRenderer } from "../components/test/VisualQuestionRenderer";

type AuditIssue = {
  severity: "Critical" | "High" | "Medium" | "Low";
  issueCode: string;
  message: string;
  recommendedAction: string;
};

type ReviewQuestion = QuestionItem & {
  slotIndex?: number;
  auditIssues?: AuditIssue[];
};

type DevQuestionsResponse = {
  questions: ReviewQuestion[];
  auditLoaded: boolean;
  generatedAt?: string | null;
};

type DevTestFormResponse = DevQuestionsResponse & {
  total: number;
  deterministic: boolean;
  version: string;
};

const severities = ["Critical", "High", "Medium", "Low"] as const;
const difficulties: DifficultyLevel[] = ["easy", "medium", "hard", "expert", "warmup"];

const severityRank: Record<string, number> = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const difficultyRank: Record<DifficultyLevel, number> = { warmup: 0, easy: 1, medium: 2, hard: 3, expert: 4 };

function highestSeverity(question: ReviewQuestion) {
  const issues = question.auditIssues ?? [];
  if (!issues.length) return "None";
  return issues.reduce((highest, issue) => (severityRank[issue.severity] > severityRank[highest] ? issue.severity : highest), issues[0].severity);
}

function asSanitized(question: ReviewQuestion): SanitizedQuestionItem {
  const { correctOption: _correctOption, explanation: _explanation, ruleExplanation: _ruleExplanation, familyId: _familyId, quality: _quality, scoring: _scoring, calibration: _calibration, auditIssues: _auditIssues, ...safe } = question;
  return safe;
}

function copyText(value: string) {
  void navigator.clipboard?.writeText(value);
}

function filterLabel(value: string) {
  return value === "" ? "Όλα" : value;
}

export function DevQuestionReviewPage() {
  const [questions, setQuestions] = useState<ReviewQuestion[]>([]);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [subtype, setSubtype] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [severity, setSeverity] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [onlyActive, setOnlyActive] = useState(true);
  const [sortBy, setSortBy] = useState("severity");
  const [mode, setMode] = useState<"bank" | "test-form">("bank");
  const [copyFallback, setCopyFallback] = useState("");
  const isLocalHost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" || window.location.hostname === "::1";

  if (!isLocalHost) {
    return (
      <main className="mx-auto w-full max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white">404</h1>
        <p className="mt-3 text-slate-300">Η σελίδα δεν βρέθηκε.</p>
      </main>
    );
  }

  function loadQuestions(url: string, nextMode: "bank" | "test-form") {
    setLoading(true);
    setError(null);
    setCopyFallback("");
    fetch(url)
      .then(async (response) => {
        const data = (await response.json()) as DevQuestionsResponse | DevTestFormResponse;
        if (!response.ok) throw new Error("Δεν μπορέσαμε να φορτώσουμε τις ερωτήσεις.");
        return data;
      })
      .then((data) => {
        setQuestions(data.questions);
        setAuditLoaded(data.auditLoaded);
        setMode(nextMode);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Άγνωστο σφάλμα.");
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    loadQuestions("/api/dev/questions", "bank");
  }, []);

  function showBank() {
    loadQuestions("/api/dev/questions", "bank");
    setSortBy("severity");
  }

  function showTestForm() {
    loadQuestions("/api/dev/test-form", "test-form");
    setSortBy("slot");
  }

  async function copyThirtyIds() {
    const list = questions
      .slice(0, 30)
      .map((question, index) => `${index + 1}. ${question.id}`)
      .join("\n");

    try {
      await navigator.clipboard.writeText(list);
      setCopyFallback("");
    } catch {
      setCopyFallback(list);
    }
  }

  const categoryOptions = Object.keys(QUESTION_SUBTYPES) as TestCategory[];
  const subtypeOptions = useMemo(() => {
    if (!category) return [...new Set(questions.map((question) => question.subtype))].sort();
    return [...QUESTION_SUBTYPES[category as TestCategory]];
  }, [category, questions]);

  const filteredQuestions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return questions
      .filter((question) => {
        const issues = question.auditIssues ?? [];
        const matchesQuery =
          !normalizedQuery ||
          question.id.toLowerCase().includes(normalizedQuery) ||
          question.prompt.toLowerCase().includes(normalizedQuery) ||
          question.subtype.toLowerCase().includes(normalizedQuery);
        const matchesCategory = !category || question.category === category;
        const matchesSubtype = !subtype || question.subtype === subtype;
        const matchesDifficulty = !difficulty || question.difficulty === difficulty;
        const matchesSeverity = !severity || issues.some((issue) => issue.severity === severity);
        const matchesIssues = !onlyIssues || issues.length > 0;
        const matchesActive = !onlyActive || question.status === "active";
        return matchesQuery && matchesCategory && matchesSubtype && matchesDifficulty && matchesSeverity && matchesIssues && matchesActive;
      })
      .sort((a, b) => {
        if (sortBy === "slot") return (a.slotIndex ?? 999) - (b.slotIndex ?? 999);
        if (sortBy === "category") return `${a.category}-${a.subtype}-${a.id}`.localeCompare(`${b.category}-${b.subtype}-${b.id}`);
        if (sortBy === "id") return a.id.localeCompare(b.id);
        if (sortBy === "difficulty") return difficultyRank[b.difficulty] - difficultyRank[a.difficulty] || a.id.localeCompare(b.id);
        return severityRank[highestSeverity(b)] - severityRank[highestSeverity(a)] || a.id.localeCompare(b.id);
      });
  }, [category, difficulty, onlyActive, onlyIssues, query, questions, severity, sortBy, subtype]);

  const distribution = useMemo(() => {
    const counts = questions.reduce<Record<string, number>>((acc, question) => {
      acc[question.category] = (acc[question.category] ?? 0) + 1;
      return acc;
    }, {});
    const issueCounts = questions.flatMap((question) => question.auditIssues ?? []).reduce<Record<string, number>>((acc, issue) => {
      acc[issue.severity] = (acc[issue.severity] ?? 0) + 1;
      return acc;
    }, {});
    return { counts, issueCounts };
  }, [questions]);

  return (
    <main className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <section className="rounded-lg border border-amber-200/30 bg-amber-200/10 p-4 text-sm font-semibold text-amber-100">
        DEV ONLY — local review console. Δεν προορίζεται για production.
      </section>

      <header className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200">Question Review Console</p>
        <h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl">Έλεγχος Ερωτήσεων</h1>
        <p className="mt-3 max-w-3xl text-slate-300">
          Dev-only εργαλείο για έλεγχο ερωτήσεων, σωστών απαντήσεων και audit issues. Δεν προορίζεται για production.
        </p>
      </header>

      <section className="glass-panel mt-6 rounded-lg p-4">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="rounded-full border border-cyan-200/25 bg-cyan-200/10 px-3 py-1 text-xs font-bold text-cyan-100">
            Mode: {mode === "test-form" ? "Τρέχον test form" : "Όλο το bank"}
          </span>
          <button type="button" onClick={showTestForm} className="min-h-11 rounded-lg border border-cyan-200/30 bg-cyan-200/10 px-4 text-sm font-semibold text-cyan-50 hover:border-cyan-200/70">
            Μόνο οι 30 ερωτήσεις του test
          </button>
          <button type="button" onClick={showBank} className="min-h-11 rounded-lg border border-white/10 bg-white/[0.06] px-4 text-sm font-semibold text-white hover:border-cyan-200/50">
            Εμφάνιση όλου του bank
          </button>
          <button type="button" onClick={showTestForm} disabled={mode !== "test-form"} className="min-h-11 rounded-lg border border-white/10 bg-white/[0.06] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45 hover:border-cyan-200/50">
            Ανανέωση 30άδας
          </button>
          <button type="button" onClick={copyThirtyIds} disabled={mode !== "test-form"} className="min-h-11 rounded-lg border border-white/10 bg-white/[0.06] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45 hover:border-cyan-200/50">
            Copy 30 IDs
          </button>
        </div>
        {copyFallback ? (
          <label className="mb-4 grid gap-2 text-sm text-slate-300">
            Clipboard fallback
            <textarea readOnly value={copyFallback} className="min-h-36 rounded-lg border border-white/10 bg-slate-950/70 p-3 font-mono text-xs text-slate-100" />
          </label>
        ) : null}
        {mode === "test-form" ? (
          <div className="mb-4 rounded-lg border border-white/10 bg-slate-950/35 p-4">
            <h2 className="text-sm font-bold text-white">Distribution</h2>
            <div className="mt-3 grid gap-2 text-sm text-slate-300 sm:grid-cols-2 lg:grid-cols-3">
              {categoryOptions.map((item) => (
                <span key={item}>{getCategoryLabel(item)}: {distribution.counts[item] ?? 0}</span>
              ))}
              <span className="font-semibold text-cyan-100">Total: {questions.length}</span>
              {auditLoaded ? (
                <>
                  <span>Critical issues: {distribution.issueCounts.Critical ?? 0}</span>
                  <span>High issues: {distribution.issueCounts.High ?? 0}</span>
                  <span>Medium issues: {distribution.issueCounts.Medium ?? 0}</span>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="grid gap-3 lg:grid-cols-[1.4fr_repeat(5,minmax(0,1fr))]">
          <label className="grid gap-1 text-sm text-slate-300">
            Αναζήτηση
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="ID, prompt ή subtype" className="min-h-11 rounded-lg border border-white/10 bg-slate-950/55 px-3 text-white outline-none focus:border-cyan-200/70" />
          </label>
          <Select label="Κατηγορία" value={category} onChange={setCategory} options={categoryOptions.map((item) => ({ value: item, label: getCategoryLabel(item) }))} />
          <Select label="Subtype" value={subtype} onChange={setSubtype} options={subtypeOptions.map((item) => ({ value: item, label: item }))} />
          <Select label="Δυσκολία" value={difficulty} onChange={setDifficulty} options={difficulties.map((item) => ({ value: item, label: item }))} />
          <Select label="Severity" value={severity} onChange={setSeverity} options={severities.map((item) => ({ value: item, label: item }))} />
          <Select label="Sort" value={sortBy} onChange={setSortBy} options={[{ value: "slot", label: "Slot" }, { value: "severity", label: "Severity" }, { value: "category", label: "Category" }, { value: "id", label: "ID" }, { value: "difficulty", label: "Difficulty" }]} />
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-200">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={onlyIssues} onChange={(event) => setOnlyIssues(event.target.checked)} className="h-4 w-4 accent-cyan-300" />
            Μόνο προβληματικές
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={onlyActive} onChange={(event) => setOnlyActive(event.target.checked)} className="h-4 w-4 accent-cyan-300" />
            Μόνο active
          </label>
          <span className="text-slate-400">
            Εμφάνιση {filteredQuestions.length} από {questions.length} ερωτήσεις. Audit issues: {auditLoaded ? "φορτώθηκαν" : "δεν φορτώθηκαν"}
          </span>
        </div>
      </section>

      {loading ? <p className="mt-8 text-slate-300">Φόρτωση ερωτήσεων...</p> : null}
      {error ? <p className="mt-8 rounded-lg border border-red-300/20 bg-red-300/10 p-4 text-red-100">{error}</p> : null}

      <section className="mt-6 grid gap-5">
        {filteredQuestions.map((question) => (
          <QuestionReviewCard key={question.id} question={question} />
        ))}
      </section>
    </main>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <label className="grid gap-1 text-sm text-slate-300">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="min-h-11 rounded-lg border border-white/10 bg-slate-950/90 px-3 text-white outline-none focus:border-cyan-200/70">
        <option value="">{filterLabel("")}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function QuestionReviewCard({ question }: { question: ReviewQuestion }) {
  const safeQuestion = asSanitized(question);
  const issues = question.auditIssues ?? [];
  const fixNote = `${question.id} — ${question.category}/${question.subtype}: ${issues.map((issue) => issue.issueCode).join(", ") || "no audit issues"}`;

  return (
    <article id={question.id} className="rounded-lg border border-white/10 bg-white/[0.045] p-4 shadow-[0_24px_80px_rgb(0_0_0_/_0.22)]">
      <div className="flex flex-wrap items-center gap-2">
        {question.slotIndex ? <span className="rounded-full border border-amber-200/25 bg-amber-200/10 px-3 py-1 text-xs font-bold text-amber-100">Slot {question.slotIndex} / 30</span> : null}
        <span className="rounded-full border border-cyan-200/25 bg-cyan-200/10 px-3 py-1 text-xs font-bold text-cyan-100">{question.id}</span>
        <span className="rounded-full border border-white/10 bg-white/[0.055] px-3 py-1 text-xs text-slate-200">{getCategoryLabel(question.category)}</span>
        <span className="rounded-full border border-white/10 bg-white/[0.055] px-3 py-1 text-xs text-slate-200">{question.subtype}</span>
        <span className="rounded-full border border-white/10 bg-white/[0.055] px-3 py-1 text-xs text-slate-200">{question.difficulty}</span>
        <span className="rounded-full border border-white/10 bg-white/[0.055] px-3 py-1 text-xs text-slate-200">{question.renderer}</span>
        <span className="rounded-full border border-white/10 bg-white/[0.055] px-3 py-1 text-xs text-slate-200">{question.status}</span>
        {issues.length ? <span className="rounded-full border border-red-200/25 bg-red-200/10 px-3 py-1 text-xs font-bold text-red-100">{highestSeverity(question)} · {issues.length}</span> : <span className="rounded-full border border-emerald-200/25 bg-emerald-200/10 px-3 py-1 text-xs font-bold text-emerald-100">No issues</span>}
      </div>

      <div className="mt-4 grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,0.72fr)]">
        <div>
          <h2 className="text-xl font-semibold leading-8 text-white">{question.prompt}</h2>
          <div className="mt-4 flex justify-center rounded-lg border border-white/10 bg-slate-950/35 p-4">
            <VisualQuestionRenderer question={safeQuestion} readOnly revealCorrectOption={question.correctOption} />
          </div>
          <AnswerGrid question={safeQuestion} readOnly revealCorrectOption={question.correctOption} />
          <p className="mt-3 text-sm font-semibold text-emerald-100">Σωστή απάντηση: {question.correctOption}</p>
          <p className="mt-2 text-sm leading-6 text-slate-300">Εξήγηση: {question.explanation}</p>
          {question.ruleExplanation ? (
            <div className="mt-3 rounded-lg border border-cyan-200/20 bg-cyan-200/10 p-3">
              <h3 className="text-sm font-bold text-cyan-100">Κανόνας</h3>
              <p className="mt-2 text-sm leading-6 text-slate-200">{question.ruleExplanation}</p>
            </div>
          ) : null}
        </div>

        <aside className="grid content-start gap-4">
          <div className="rounded-lg border border-white/10 bg-slate-950/35 p-4">
            <h3 className="text-sm font-bold text-white">Audit issues</h3>
            {issues.length ? (
              <ul className="mt-3 grid gap-3">
                {issues.map((issue, index) => (
                  <li key={`${issue.issueCode}-${index}`} className="rounded-lg border border-white/10 bg-white/[0.045] p-3 text-sm">
                    <div className="flex flex-wrap gap-2">
                      <span className="font-bold text-cyan-100">{issue.severity}</span>
                      <span className="text-slate-400">{issue.issueCode}</span>
                    </div>
                    <p className="mt-2 leading-6 text-slate-300">{issue.message}</p>
                    <p className="mt-2 text-xs font-semibold text-slate-400">Recommended: {issue.recommendedAction}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-slate-400">Δεν εντοπίστηκαν audit issues.</p>
            )}
          </div>

          <div className="rounded-lg border border-white/10 bg-slate-950/35 p-4">
            <h3 className="text-sm font-bold text-white">Quality metadata</h3>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <Meta label="ambiguityRisk" value={question.quality.ambiguityRisk} />
              <Meta label="visualClarity" value={question.quality.visualClarity} />
              <Meta label="mobileFriendly" value={String(question.quality.mobileFriendly)} />
              <Meta label="requiresManualAsset" value={String(question.quality.requiresManualAsset)} />
              <Meta label="reviewStatus" value={question.quality.reviewStatus} />
              <Meta label="weight" value={String(question.scoring.weight)} />
            </dl>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => copyText(question.id)} className="min-h-11 rounded-lg border border-white/10 bg-white/[0.06] px-4 text-sm font-semibold text-white hover:border-cyan-200/50">
              Copy ID
            </button>
            <button type="button" onClick={() => copyText(fixNote)} className="min-h-11 rounded-lg border border-white/10 bg-white/[0.06] px-4 text-sm font-semibold text-white hover:border-cyan-200/50">
              Copy fix note
            </button>
            <a href={`#${question.id}`} className="inline-flex min-h-11 items-center rounded-lg border border-white/10 bg-white/[0.06] px-4 text-sm font-semibold text-white hover:border-cyan-200/50">
              Preview as user
            </a>
          </div>
        </aside>
      </div>
    </article>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-semibold text-slate-200">{value}</dd>
    </>
  );
}
